import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import ImageLinks, { IImageLinksProps } from './ImageLink';
import Nav, { MMNavItem } from '../MegaMenus/Nav';
import AnimatedArcs from '../AnimatedArcs';
import calianLogo from '../../../assets/images/logo-colour.svg';
import Image from 'tbk-components/src/components/Image';

export interface IMegaMenuLeftColProps {
	leftColumnHeading?: string;
	/**
	 * @control richtext
	 */
	intro?: string;
}

export interface IMegaMenuRightColProps {
	rightColumnHeading?: string;
	/**
	 * @control richtext
	 */
	content?: string;
	cta?: ILink;
}

export interface IMegaMenuCorporateProps extends IBaseElementProps {
	leftColumn?: IMegaMenuLeftColProps;
	menuType?: 'industries' | 'divisions' | 'list';
	/**
	 * This only works with Menu Type 'list'
	 */
	listStyle?: 'default' | 'five-column';

	centerColumn?: IImageLinksProps;
	rightColumn?: IMegaMenuRightColProps;
	/**
	 * Select a WordPress menu to supply the nav items for this Mega Menu.
	 * @control NavItem[]
	 */
	listMenu?: MMNavItem[];
	slogan?: string;
	/**
	 * Line Animation Delay in Milliseconds - only used when list menu has content
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixels - only used when list menu has content
	 */
	lineAnimationOffset?: number;
}

const hasValue = (obj) => {
	if (!obj) {
		return false;
	}

	return Object.values(obj).every(
		(value) => value !== undefined && value !== null,
	);
};

/**
 * Calian Mega Menus Corporate
 * @icon format-gallery
 * @block
 */
const MegaMenuCorporate: React.FC<IMegaMenuCorporateProps> = ({
	id,
	className,
	classNames = [],
	leftColumn,
	menuType,
	centerColumn,
	rightColumn,
	listMenu,
	slogan,
	lineAnimationDelay = 1000,
	lineAnimationOffset = 150,
	listStyle = 'default',
}) => {
	const mmRef = useRef<HTMLDivElement>(null);

	// Dynamically set the max height of the mega menu
	useEffect(() => {
		const calcMegaMenuHeight = () => {
			if (!mmRef.current) {
				return;
			}

			if (window.innerWidth >= 1200) {
				const header = document.querySelector(
					'.calian-header',
				) as HTMLElement;
				mmRef.current.style.maxHeight = `${Math.floor(
					window.innerHeight - header?.offsetHeight,
				)}px`;
			} else {
				mmRef.current.style.maxHeight = '';
			}
		};

		calcMegaMenuHeight();
		window.addEventListener('resize', calcMegaMenuHeight);

		return () => {
			window.removeEventListener('resize', calcMegaMenuHeight);
		};
	}, []);

	return (
		<div
			id={id}
			className={cn(
				className || [
					'mega-menu-corporate__container',
					menuType === 'divisions' &&
						!listMenu &&
						'mega-menu-corporate__narrow-center',
					listMenu && 'mega-menu-corporate__list-menu-only',
					listStyle === 'five-column' &&
						'mega-menu-corporate__five-column',
					...classNames,
				],
			)}
			ref={mmRef}
		>
			<div className="mega-menu-corporate__container-inner">
				{listMenu && listMenu?.length > 0 && (
					<>
						<Nav
							navItems={listMenu}
							collapseEnabled={false}
							dropdownEnabled={false}
						/>
						<div className="mega-menu-corporate__bottom">
							<Image src={calianLogo} width={180} height={65} />
							{slogan && <span>{slogan}</span>}
						</div>
					</>
				)}
				{!listMenu && (
					<>
						{hasValue(leftColumn) && (
							<div className="mega-menu-corporate__left">
								<div className="mega-menu-corporate__left-inner">
									{leftColumn?.leftColumnHeading && (
										<span>
											{leftColumn.leftColumnHeading}
										</span>
									)}
									{leftColumn?.intro && (
										<div
											className="mega-menu-corporate__left-intro"
											dangerouslySetInnerHTML={{
												__html: leftColumn.intro,
											}}
										/>
									)}
								</div>
							</div>
						)}
						{centerColumn?.imageLinkGroup?.length > 0 && (
							<div className="mega-menu-corporate__center">
								<div className="mega-menu-corporate__center-inner">
									<ImageLinks {...centerColumn} />
								</div>
							</div>
						)}
						{hasValue(rightColumn) && (
							<div className="mega-menu-corporate__right">
								<div className="mega-menu-corporate__right-inner">
									{rightColumn?.rightColumnHeading && (
										<span>
											{rightColumn.rightColumnHeading}
										</span>
									)}
									{rightColumn?.content && (
										<div
											className="mega-menu-corporate__left-intro"
											dangerouslySetInnerHTML={{
												__html: rightColumn.content,
											}}
										/>
									)}
									{rightColumn?.cta?.href && (
										<Button
											{...rightColumn.cta}
											className="btn btn-primary btn-sm"
										/>
									)}
								</div>
							</div>
						)}
					</>
				)}
			</div>
			{listMenu && listMenu?.length > 0 && (
				<AnimatedArcs
					arcsStyle={'Component - Two Lines'}
					arcsPosition="Right"
					arcOneColor="var(--bs-light-bg)"
					arcOneStrokeWidth={3}
					arcTwoColor="var(--bs-light-bg)"
					arcTwoStrokeWidth={3}
					animationDelay={lineAnimationDelay}
					animationOffset={lineAnimationOffset}
				/>
			)}
		</div>
	);
};

export default MegaMenuCorporate;
